import React, { useEffect, useRef, useState } from "react";
import { Input, Table, Tabs, ConfigProvider, message, Spin, Tag, Button, Popconfirm } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import Column from "antd/es/table/Column";
import { formatDateTime, isNotNullUndefined } from "../helpers/common";
import { useFetchTripList, useTripCancel } from "../services/trip.service";
import Pagination from "../helpers/pagination";
import { storage } from "../utils/storage";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CSVLink } from "react-csv";
import { useAuth } from "../context/AuthContext";
import CreateTripDrawer from "./create-trip-drawer";
import AdminHardDeleteTripModal from "./admin-hard-delete-trip-modal";

const Triplist: React.FC = () => {

    const { Search } = Input;
    const { TabPane } = Tabs;
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const canCreateTrip = hasPermission("TRIPS", "CREATE");
    const canDeleteTrip = hasPermission("TRIPS", "DELETE");
    const [searchParams, setSearchParams] = useSearchParams();
    const [tripListData, setTripList] = useState([]);
    const [createTripOpen, setCreateTripOpen] = useState(false);
    const tabFilterType = useRef('Pending');
    const [activeTabKey, setActiveTabKey] = useState("1");
    const [searchText, setSearchText] = useState("");
    const pageNumber = useRef(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setIsLoading] = useState(false);
    const searchValue = useRef<string>("");
    const [hardDeleteTripId, setHardDeleteTripId] = useState<string | null>(null);

    const tabItems: any = [
        {
            key: 1,
            name: 'Pending Trips',
            value: 'Pending'
        },
        {
            key: 2,
            name: 'Upcoming Trips',
            value: 'Upcoming',
        },
        {
            key: 3,
            name: 'Running Trips',
            value: 'Running'
        },
        {
            key: 4,
            name: 'Completed Trips',
            value: 'Completed'
        },
        {
            key: 5,
            name: 'All Trips',
            value: 'All'
        },
        {
            key: 6,
            name: 'Incomplete Trips',
            value: 'Incomplete'
        }
    ];

    useEffect(() => {
        const userRoleId = parseInt(storage.getUserRoleId() ?? "0");
        if (![1, 4].includes(userRoleId)) {
            navigate("/");
            return;
        }

        const tabParam = (searchParams.get("tab") ?? "pending").toLowerCase();
        const query = searchParams.get("q") ?? "";
        const tabValueMap: Record<string, string> = {
            pending: "Pending",
            upcoming: "Upcoming",
            running: "Running",
            completed: "Completed",
            all: "All",
            incomplete: "Incomplete"
        };
        const tabKeyMap: Record<string, string> = {
            Pending: "1",
            Upcoming: "2",
            Running: "3",
            Completed: "4",
            All: "5",
            Incomplete: "6"
        };

        const filterType = tabValueMap[tabParam] ?? "Pending";
        tabFilterType.current = filterType;
        setActiveTabKey(tabKeyMap[filterType] ?? "1");

        searchValue.current = query;
        setSearchText(query);
        pageNumber.current = 1;
        fetchTripList(pageNumber.current, pageSize, filterType, query);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams.toString(), pageSize]);

    const fetchTripList = async (pageNumber: number, pageSize: number, tabFilterType: string, searchValue: string) => {
        setIsLoading(true);
        setTripList([]);
        fetchTripListMutation.mutate({ pageNumber: pageNumber, pageSize: pageSize, filterType: tabFilterType, searchFilter: searchValue });
    };

    const fetchTripListMutation = useFetchTripList({
        onSuccess: (response: any) => {
            if (response.statusCode === 200) {
                if (isNotNullUndefined(response.data) && response.data.length > 0) {
                    const formattedData = response.data.map((data: any) => ({
                        ...data,
                        tripStartDate: formatDateTime(data.tripStartDate, "DD/MM/YYYY"),
                        tripEndDate: formatDateTime(data.tripEndDate, "DD/MM/YYYY"),
                        scheduleDate: formatDateTime(data.scheduleDate, "DD/MM/YYYY")
                    }));
                    setTripList(formattedData);
                    const recordCount = formattedData[0].recordCount;
                    setTotalPages(Math.ceil(recordCount / pageSize));
                    setIsLoading(false);
                } else {
                    setTripList([]);
                    setIsLoading(false);
                }
            } else {
                message.error(response.statusMessage);
                setIsLoading(false);
            }
        },
        onError: (error: any) => {
            message.error(error.message);
            setIsLoading(false);
        }
    });

    const cancelTripMutation = useTripCancel({
        onSuccess: (response: any) => {
            if (response.statusCode === 200) {
                message.success('Trip cancelled successfully.');
                fetchTripList(pageNumber.current, pageSize, tabFilterType.current, searchValue.current ?? "");
            } else {
                message.error(response.statusMessage);
            }
        },
        onError: (error: any) => {
            message.error(error.message);
        }
    });

    const onCancelStuckTrip = (tripId: string) => {
        cancelTripMutation.mutate({ tripId, cancelledReason: "Cancelled by admin -- trip never completed booking." });
    };

    const tabClicked = (key: number) => {
        const selectedFilter = tabItems.find((x: any) => x.key === key)?.value ?? 'Pending';
        const tabParamMap: Record<string, string> = {
            Pending: "pending",
            Upcoming: "upcoming",
            Running: "running",
            Completed: "completed",
            All: "all",
            Incomplete: "incomplete"
        };

        const next = new URLSearchParams(searchParams);
        next.set("tab", tabParamMap[selectedFilter] ?? "pending");
        if (searchValue.current) {
            next.set("q", searchValue.current);
        } else {
            next.delete("q");
        }
        setSearchParams(next);
    };

    const handlePageChange = (newPage: number) => {
        pageNumber.current = newPage;
        let size = pageSize;
        setPageSize(size);
        fetchTripList(pageNumber.current, pageSize, tabFilterType.current, searchValue.current ?? "");
    };

    const onRowClick = (record: any) => {
        navigate(`/trips/${record.tripId}/${record.scheduleId}`);
    };

    const onSearch = (value: any) => {
        const search = String(value ?? "");
        searchValue.current = search;
        setSearchText(search);
        const next = new URLSearchParams(searchParams);
        if (!next.get("tab")) {
            next.set("tab", "pending");
        }
        if (search) {
            next.set("q", search);
        } else {
            next.delete("q");
        }
        setSearchParams(next);
    };

    const headers = [
        { label: "AgentEmail", key: "agentMasterEmail" },
        { label: "Attendant", key: "agentName" },
        { label: "Date", key: "scheduleDate" },
        { label: "# of Hours", key: "totalHours" },
        { label: "Start Time", key: "tripStartDate" },
        { label: "End Time", key: "tripEndDate" },
        { label: "Status", key: "tripStatus" },
        { label: "Expected Income", key: "expectedIncome" },
        { label: "Actual Duration", key: "actualHours" },
        { label: "Customer Feedback", key: "comment" }
    ];

    const emptyState = (
        <div className="ant-empty">
            <div className="ant-empty-image">
                <svg width="64" height="41" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
                    <g transform="translate(0 1)" fill="none" fillRule="evenodd">
                        <ellipse fill="#f5f5f5" cx="32" cy="33" rx="32" ry="7"></ellipse>
                        <g fillRule="nonzero" stroke="#d9d9d9">
                            <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"></path>
                            <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z" fill="#fafafa"></path>
                        </g>
                    </g>
                </svg>
            </div>
            <div className="ant-empty-description">No data</div>
        </div>
    );

    const renderTripTable = (options?: { wide?: boolean; showCancel?: boolean }) => (
        <>
            <Table dataSource={tripListData} className={options?.wide ? "userlist-tabs-table cursor-pointer" : "userlist-table cursor-pointer"} bordered pagination={{ pageSize: pageSize, position: ['none'] }}
                onRow={(record) => ({
                    onClick: () => onRowClick(record)
                })}
                locale={{
                    emptyText: loading ? <Spin /> : emptyState
                }}
            >
                <Column title="Traveller Name" width={options?.wide ? 200 : undefined} dataIndex="userName" key="userName" align="center" />
                <Column title="Attendant Name" width={options?.wide ? 200 : undefined} dataIndex="agentName" key="agentName" align="center" />
                <Column title="Cities" width={options?.wide ? 300 : undefined} dataIndex="cities" key="cities" align="center" />
                <Column title="Trip Status" width={200} dataIndex="tripStatus" key="tripStatus" align="center"
                    render={(tripStatus: string) => (
                        <>
                            {tripStatus ?
                                <Tag key={tripStatus} color={tripStatus === "Completed" ? "green" : tripStatus === "Pending" ? "orange" : tripStatus === "Upcoming" ? "blue" : tripStatus === "Running" ? "purple" : tripStatus === "Expired" ? "magenta" : "red"} className="m-0 pl-1 pr-1">
                                    {tripStatus.toUpperCase()}
                                </Tag> : ""}
                        </>
                    )} />
                <Column title="Payment Status" width={200} dataIndex="paymentStatus" key="paymentStatus" align="center"
                    render={(paymentStatus: string) => (
                        <>
                            {paymentStatus ?
                                <Tag key={paymentStatus} color={paymentStatus === "Platform Fee Paid" ? "blue" : (paymentStatus === "Fully Paid" || paymentStatus === "Paid") ? "green" : paymentStatus === "Partially Paid" ? "purple" : (paymentStatus === "Overdue" || paymentStatus === "Payment Failed") ? "red" : "orange"} className="m-0 pl-1 pr-1">
                                    {paymentStatus.toUpperCase()}
                                </Tag> : ""}
                        </>
                    )} />
                <Column title="Trip Start Date" width={options?.wide ? 200 : undefined} dataIndex="tripStartDate" key="tripStartDate" align="center" />
                <Column title="Trip End Date" width={options?.wide ? 200 : undefined} dataIndex="tripEndDate" key="tripEndDate" align="center" />
                {canDeleteTrip ? (
                    <Column title="Actions" key="actions" align="center"
                        render={(_: any, record: any) => (
                            <div className="flex gap-2 justify-center" onClick={(e) => e.stopPropagation()}>
                                {options?.showCancel ? (
                                    <Popconfirm
                                        title="Cancel this trip?"
                                        description="This trip never completed booking. Cancel it?"
                                        onConfirm={() => onCancelStuckTrip(record.tripId)}
                                        okText="Yes, cancel"
                                        cancelText="No"
                                    >
                                        <Button size="small">Cancel</Button>
                                    </Popconfirm>
                                ) : null}
                                <Button size="small" danger onClick={() => setHardDeleteTripId(record.tripId)}>
                                    Delete
                                </Button>
                            </div>
                        )} />
                ) : null}
            </Table>
            <div>
                <Pagination
                    currentPage={pageNumber.current}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
            </div>
        </>
    );

    return (
        <div>
            <div className="flex justify-between align-middle pb-5">
                <h2 className="text-3xl font-semibold text-black">Trip List</h2>
                <div className="flex justify-between gap-5">
                    {canCreateTrip && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            size="large"
                            onClick={() => setCreateTripOpen(true)}
                            className="bg-black text-white hover:!border-black hover:!bg-transparent hover:!text-black hover:transition hover:duration-500 hover:ease-in-out"
                        >
                            Create Trip
                        </Button>
                    )}
                    <CSVLink
                        data={tripListData}
                        headers={headers}
                        filename="data_report.csv"
                        className=" inline-block bg-black text-white font-semibold py-2 px-4 rounded hover:bg-transparent border-solid border-[1px] border-black hover:!text-black hover:!border-black"
                        target="_blank"
                    >
                        Export as CSV
                    </CSVLink>
                    <div className="w-80 max-w-80">

                        <Search
                            placeholder="Search trip"
                            value={searchText}
                            size="large"
                            enterButton
                            className="search-field"
                            onChange={(e) => setSearchText(e.target.value)}
                            onSearch={onSearch}
                        />
                    </div>
                </div>
            </div>
            <ConfigProvider
                theme={{
                    components: {
                        Tabs: {
                            titleFontSizeLG: 16,
                        },
                    }
                }}
            >
                <Tabs activeKey={activeTabKey} onTabClick={(e) => { tabClicked(parseInt(e)) }}>
                    <TabPane tab="Pending Trips" key="1">
                        {renderTripTable()}
                    </TabPane>
                    <TabPane tab="Upcoming Trips" key="2">
                        {renderTripTable()}
                    </TabPane>
                    <TabPane tab="Running Trips" key="3">
                        {renderTripTable()}
                    </TabPane>
                    <TabPane tab="Completed Trips" key="4">
                        {renderTripTable()}
                    </TabPane>
                    <TabPane tab="All Trips" key="5">
                        {renderTripTable({ wide: true })}
                    </TabPane>
                    <TabPane tab="Incomplete Trips" key="6">
                        {renderTripTable({ showCancel: true })}
                    </TabPane>
                </Tabs>
            </ConfigProvider>

            <CreateTripDrawer
                open={createTripOpen}
                onClose={() => setCreateTripOpen(false)}
                onCreated={() => fetchTripList(pageNumber.current, pageSize, tabFilterType.current, searchValue.current ?? "")}
            />

            <AdminHardDeleteTripModal
                open={hardDeleteTripId !== null}
                tripId={hardDeleteTripId ?? ""}
                onClose={() => setHardDeleteTripId(null)}
                onDeleted={() => {
                    setHardDeleteTripId(null);
                    fetchTripList(pageNumber.current, pageSize, tabFilterType.current, searchValue.current ?? "");
                }}
            />
        </div>
    );
};

export default Triplist;
// Helper function to convert file to base64

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
    });
}
